import { RestClient } from "./restClient";
import { WorkspaceComment } from "@/models/workspace/WorkspaceComment";
import RequestMethod from "@/models/client/RequestMethod";
import RestResponse from "@/models/client/RestResponse";
import { crudService, CrudServiceType } from "./crudService";

// Types for request payloads

export interface UpdateCommentContentRequest {
  content: string;
  fileId?: string;
}

export interface UpdateCommentReactionRequest {
  reaction: string;
  fileId?: string;
}

export interface AddCommentReplyRequest {
  content: string;
  workspaceId: string;
  fileId?: string;
}

// Define the service type with base CRUD operations and additional methods
export type CommentServiceType = CrudServiceType<WorkspaceComment> & {
  getCommentsByFileId: (
    workspaceId: string,
    fileId: string
  ) => RestResponse<WorkspaceComment[]>;
  getGeneralWorkspaceComments: (
    workspaceId: string
  ) => RestResponse<WorkspaceComment[]>;
  updateCommentContent: (
    id: string,
    request: UpdateCommentContentRequest
  ) => RestResponse<WorkspaceComment>;
  updateCommentReaction: (
    id: string,
    request: UpdateCommentReactionRequest
  ) => RestResponse<WorkspaceComment>;
  addCommentReply: (
    id: string,
    request: AddCommentReplyRequest
  ) => RestResponse<WorkspaceComment>;
  deleteComment: (id: string) => RestResponse<boolean>;
};

const commentServices = (
  client: RestClient,
  path: string
): CommentServiceType => ({
  ...crudService<WorkspaceComment>(client, path),

  getCommentsByFileId: async (workspaceId: string, fileId: string) =>
    await client.fetch<WorkspaceComment[]>(
      RequestMethod.GET,
      `${path}/${workspaceId}/${fileId}`
    ),

  getGeneralWorkspaceComments: async (workspaceId: string) =>
    await client.fetch<WorkspaceComment[]>(
      RequestMethod.GET,
      `${path}/workspace/${workspaceId}/general`
    ),

  updateCommentContent: async (
    id: string,
    request: UpdateCommentContentRequest
  ) =>
    await client.fetch<WorkspaceComment>(
      RequestMethod.PUT,
      `${path}/${id}/content`,
      request
    ),

  updateCommentReaction: async (
    id: string,
    request: UpdateCommentReactionRequest
  ) =>
    await client.fetch<WorkspaceComment>(
      RequestMethod.PUT,
      `${path}/${id}/reaction`,
      request
    ),

  addCommentReply: async (id: string, request: AddCommentReplyRequest) =>
    await client.fetch<WorkspaceComment>(
      RequestMethod.POST,
      `${path}/${id}/reply`,
      request
    ),

  deleteComment: async (id: string) =>
    await client.fetch<boolean>(RequestMethod.DELETE, `${path}/${id}`),
});

export default commentServices;
